import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileFrame from '@/components/MobileFrame';
import { Icon } from '@/components/Icons';
import { tpl, useT } from '@/lib/i18n';
import { useOnboardingStore } from '@/lib/store';
import { submitLiveness } from '@/lib/onboarding-api';
import { blobToBase64 } from '@/lib/upload';
import { usePeruriErrorTranslator } from '@/lib/peruri-errors';

type Phase = 'ready' | 'permission' | 'running' | 'processing' | 'done' | 'error';

const STEP_KEYS = ['center', 'blink', 'right', 'left', 'smile'] as const;
type StepKey = (typeof STEP_KEYS)[number];

export default function LivenessScreen() {
    const t = useT();
    const navigate = useNavigate();
    const setLivenessBlob = useOnboardingStore((s) => s.setLivenessBlob);
    const setVerification = useOnboardingStore((s) => s.setVerification);
    const translateErr = usePeruriErrorTranslator();

    const [phase, setPhase] = useState<Phase>('ready');
    const [step, setStep] = useState(0);
    const [errMsg, setErrMsg] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const recorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);

    const steps: Array<{ key: StepKey; label: string }> = [
        { key: 'center', label: t.livenessLookCenter },
        { key: 'blink', label: t.livenessBlink },
        { key: 'right', label: t.livenessTurnRight },
        { key: 'left', label: t.livenessTurnLeft },
        { key: 'smile', label: t.livenessSmile },
    ];

    useEffect(() => {
        return () => {
            streamRef.current?.getTracks().forEach((t) => t.stop());
            recorderRef.current?.state === 'recording' && recorderRef.current?.stop();
        };
    }, []);

    const startCamera = async (): Promise<MediaStream | null> => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'user', width: 640, height: 480 },
                audio: true,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
            return stream;
        } catch (e) {
            setErrMsg(e instanceof Error ? e.message : t.livenessAllowCamera);
            setPhase('error');
            return null;
        }
    };

    const startRecording = async () => {
        const stream = streamRef.current ?? (await startCamera());
        if (!stream) return;

        const mimeCandidates = [
            'video/webm;codecs=vp9',
            'video/webm;codecs=vp8',
            'video/webm',
            'video/mp4',
        ];
        const mime = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m)) ?? '';

        const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
        recorderRef.current = recorder;
        chunksRef.current = [];

        recorder.ondataavailable = (e) => {
            if (e.data.size > 0) chunksRef.current.push(e.data);
        };
        recorder.onstop = async () => {
            const videoMime = mime || 'video/webm';
            // Strip codec suffix: "video/webm;codecs=vp9" → "video/webm"
            // Backend expect mime tanpa codec param supaya simple validation.
            const cleanMime = videoMime.split(';')[0].trim();
            const blob = new Blob(chunksRef.current, { type: videoMime });
            setLivenessBlob(blob);
            streamRef.current?.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
            setPhase('processing');
            try {
                // Convert ke base64 di browser — Peruri expect base64 string,
                // dan payload JSON lebih konsisten daripada multipart upload.
                const base64 = await blobToBase64(blob);
                const result = await submitLiveness(base64, cleanMime);
                if (result.resultCode === '0') {
                    // Peruri bilang sukses → langsung lanjut ke signature, tidak ditahan.
                    setVerification({ liveness: 'ok' });
                    setPhase('done');
                    navigate('/onboarding/signature');
                } else {
                    // Peruri balas non-zero — tampilkan kode + pesan persis dari Peruri
                    // supaya user (atau admin) tahu apa yang ditolak.
                    const msg = translateErr('videoVerification', result.resultCode, result.resultDesc);
                    const leftHint = result.attemptsLeft > 0 ? ` (sisa ${result.attemptsLeft}x percobaan)` : '';
                    const codeHint = ` [code ${result.resultCode}]`;
                    setErrMsg(`${msg}${leftHint}${codeHint}`);
                    setPhase('error');
                }
            } catch (e) {
                setErrMsg(e instanceof Error ? e.message : t.somethingWrong);
                setPhase('error');
            }
        };

        recorder.start();
        setPhase('running');
        setStep(0);
    };

    useEffect(() => {
        if (phase !== 'running') return;
        if (step >= steps.length) {
            recorderRef.current?.stop();
            return;
        }
        // 1 detik per step × 5 step = ~5 detik total rekaman (sesuai spec Peruri).
        const id = setTimeout(() => setStep((s) => s + 1), 1000);
        return () => clearTimeout(id);
    }, [phase, step, steps.length]);

    const handleStart = async () => {
        setErrMsg(null);
        setPhase('permission');
        const stream = await startCamera();
        if (stream) await startRecording();
    };

    const handleRetry = () => {
        setErrMsg(null);
        setPhase('ready');
        setStep(0);
    };

    const progressPct = phase === 'done' ? 100 : (step / steps.length) * 100;

    return (
        <MobileFrame
            progress={(5 / 7) * 100}
            showBack
            stepLabel={tpl(t.stepOf, { current: 5, total: 7 })}
        >
            <div
                className="fade-in"
                style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '8px 24px 28px',
                }}
            >
                <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>
                    {t.livenessTitle}
                </h1>
                <p
                    style={{
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: 'var(--ink-500)',
                        marginBottom: 20,
                    }}
                >
                    {t.livenessSubtitle}
                </p>

                {/* Camera frame */}
                <div
                    style={{
                        position: 'relative',
                        alignSelf: 'center',
                        width: 280,
                        height: 340,
                        borderRadius: 140,
                        overflow: 'hidden',
                        background: '#0A0E14',
                        marginBottom: 20,
                    }}
                >
                    <video
                        ref={videoRef}
                        muted
                        playsInline
                        style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transform: 'scaleX(-1)', // mirror supaya natural
                        }}
                    />

                    {phase === 'ready' && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                background:
                                    'radial-gradient(ellipse at center 40%, #3A3F4A 0%, #1A1D25 60%, #0A0E14 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'rgba(255,255,255,0.6)',
                                fontSize: 13,
                            }}
                        >
                            {t.livenessReady}
                        </div>
                    )}

                    {(phase === 'running' || phase === 'processing' || phase === 'done') && (
                        <svg
                            style={{ position: 'absolute', inset: 0 }}
                            width="280"
                            height="340"
                            viewBox="0 0 280 340"
                        >
                            <defs>
                                <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="0%" stopColor="#3DDC97" />
                                    <stop offset="100%" stopColor="#0B3D91" />
                                </linearGradient>
                            </defs>
                            <ellipse
                                cx="140"
                                cy="170"
                                rx="130"
                                ry="158"
                                fill="none"
                                stroke="rgba(255,255,255,0.1)"
                                strokeWidth="4"
                            />
                            <ellipse
                                cx="140"
                                cy="170"
                                rx="130"
                                ry="158"
                                fill="none"
                                stroke="url(#ringGrad)"
                                strokeWidth="4"
                                strokeDasharray={Math.PI * 2 * 130}
                                strokeDashoffset={Math.PI * 2 * 130 * (1 - progressPct / 100)}
                                strokeLinecap="round"
                                transform="rotate(-90 140 170)"
                                style={{ transition: 'stroke-dashoffset 0.4s ease-out' }}
                            />
                        </svg>
                    )}

                    {phase === 'done' && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'rgba(21,128,61,0.25)',
                                animation: 'fadeIn 0.3s',
                            }}
                        >
                            <div
                                style={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    background: '#15803D',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 0 0 8px rgba(21,128,61,0.3)',
                                }}
                            >
                                <Icon name="check" size={44} color="white" strokeWidth={3} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Current instruction */}
                <div
                    style={{
                        textAlign: 'center',
                        minHeight: 52,
                        padding: '12px 16px',
                        borderRadius: 12,
                        background: 'var(--ink-50)',
                        border: '1px solid var(--ink-200)',
                        marginBottom: 20,
                    }}
                >
                    <div
                        style={{
                            fontSize: 12,
                            color: 'var(--ink-500)',
                            fontWeight: 600,
                            marginBottom: 2,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                        }}
                    >
                        {phase === 'ready' && 'Persiapan'}
                        {phase === 'permission' && 'Izin Kamera'}
                        {phase === 'running' && `Langkah ${step + 1} / ${steps.length}`}
                        {phase === 'processing' && 'Memproses'}
                        {phase === 'done' && 'Selesai'}
                        {phase === 'error' && 'Gagal'}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink-900)' }}>
                        {phase === 'ready' && t.livenessReady}
                        {phase === 'permission' && t.livenessAllowCamera}
                        {phase === 'running' && steps[step]?.label}
                        {phase === 'processing' && t.livenessProcessing}
                        {phase === 'done' && 'Verifikasi wajah berhasil'}
                        {phase === 'error' && (errMsg ?? t.somethingWrong)}
                    </div>
                </div>

                <div style={{ flex: 1 }} />

                {phase === 'ready' && (
                    <button
                        className="btn btn-primary"
                        style={{ width: '100%' }}
                        onClick={handleStart}
                    >
                        {t.livenessStart}
                    </button>
                )}
                {(phase === 'running' ||
                    phase === 'processing' ||
                    phase === 'permission') && (
                    <button className="btn btn-secondary" style={{ width: '100%' }} disabled>
                        <div className="spinner dark" />
                        {phase === 'processing'
                            ? t.livenessProcessing
                            : 'Mendeteksi wajah...'}
                    </button>
                )}
                {phase === 'error' && (
                    <button
                        className="btn btn-primary"
                        style={{ width: '100%' }}
                        onClick={handleRetry}
                    >
                        <Icon name="refresh" size={16} color="white" />
                        {t.livenessRetry}
                    </button>
                )}
                {phase === 'done' && (
                    <button
                        className="btn btn-primary"
                        style={{ width: '100%' }}
                        onClick={() => navigate('/onboarding/signature')}
                    >
                        {t.continue}
                        <Icon name="arrowRight" size={18} />
                    </button>
                )}
            </div>
        </MobileFrame>
    );
}
